/**
 * Help section copy (Spanish). Same keys as helpSections.en.ts
 */
import type { HelpSectionMap } from './helpSections.en';

const helpSectionsEs: HelpSectionMap = {
  'getting-started': {
    intro:
      'Aquí tiene lo esencial para empezar. Siga estos pasos o explore la barra lateral.',
    stepsTitle: 'Sus primeros pasos',
    step1: 'Abra **Resumen** para ver su panel y la actividad reciente.',
    step2: 'Abra **Mis tareas** para ver todas las incidencias asignadas a usted.',
    step3: 'Pulse un **Proyecto** en la barra lateral para abrir su tablero, backlog, hoja de ruta y más.',
    stepPm:
      'Abra **Gestión de proyectos** para ver todos los proyectos y una vista de hoja de ruta (etapa actual, próximo despliegue, fecha planificada).',
    step4: 'Use el botón **Crear** de la cabecera para añadir una incidencia.',
    step5:
      'Use el **interruptor de tema** (sol/luna) en la cabecera para modo claro u oscuro.',
    outro:
      'Use los enlaces de la izquierda para ir a cualquier tema, o el recorrido rápido arriba.',
  },
  notifications: {
    intro:
      'El icono de campana muestra notificaciones en la app. Pulse para abrir el centro y marcar como leídas.',
    whatTitle: 'Sobre qué se le notifica',
    n1: 'Cuando alguien le asigna una incidencia',
    n2: 'Cuando le **@mencionan** en un comentario',
    n3: 'Cambios de estado, comentarios y recordatorios de fecha límite',
    n4: 'Invitaciones a proyecto (si su organización las usa)',
    prefsTitle: 'Preferencias',
    prefsBody:
      'En perfil o ajustes de cuenta elija qué notificaciones recibe por correo y en la app (p. ej. solo asignaciones o también menciones).',
    sessionsBody:
      'Desde su perfil también puede ver **Sesiones activas** y cerrar sesión en otros dispositivos.',
    tip: 'Abra la campana y pulse «Marcar todo como leído» para vaciar la lista.',
  },
  'keyboard-shortcuts': {
    intro:
      'Úselos con el contenido principal enfocado. En Mac **Cmd**, en Windows/Linux **Ctrl**.',
    k1: '**Cmd/Ctrl + K** o **/** – Buscar incidencias y proyectos (spotlight). Desactivado con un modal de incidencia abierto.',
    k2: '**Cmd/Ctrl + I** – Abrir crear incidencia',
    k3: '**Cmd/Ctrl + Enter** – Enviar el formulario de crear incidencia',
    k4: '**Cmd/Ctrl + S** – Guardar (en el editor de documentos)',
    k5: '**Escape** – Cerrar modal o búsqueda',
    tip: 'Pulse / o Cmd/Ctrl + K para buscar sin salir de la página. La vista lista usa desplazamiento virtualizado para listas grandes.',
  },
  projects: {
    intro:
      'Los proyectos agrupan incidencias, sprints e informes. Abra cualquier proyecto del que sea miembro desde la barra lateral.',
    createTitle: 'Cómo crear un proyecto',
    createBody:
      'Vaya a **Gestión de proyectos** y pulse **Crear proyecto**. Añada nombre, descripción y categoría. Puede usar una plantilla para rellenar flujos y ajustes.',
    membersTitle: 'Miembros',
    membersLead:
      'Abra **Gestión de proyectos** para ver todos los proyectos, exportar a CSV y la **Vista de hoja de ruta** (etapa, próximo despliegue, fecha por proyecto). Abra un proyecto y use **Gestionar miembros** para:',
    m1: 'Añadir personas y asignar rol (Admin, Miembro o Visor).',
    m2:
      'Añadir un **grupo** (Ajustes de administración → Grupos) para que todos compartan el mismo rol.',
    m3: 'Cambiar un rol o quitar a alguien.',
    rolesHint:
      'Visor = solo lectura. Miembro = editar incidencias y comentar. Admin = miembros y ajustes. Se necesita al menos un admin de proyecto.',
    notePlan:
      'La creación puede limitarse por el plan de la organización (Free, Pro, Enterprise). Vea Planes y límites abajo.',
    memberOnly:
      'Solo administradores y gestores de proyecto pueden crear proyectos. Pida a su admin o use el flujo de solicitud de acceso.',
  },
  issues: {
    intro:
      'Las incidencias son el trabajo que sigue: tareas, errores, historias, épicas y subtareas.',
    createTitle: 'Cómo crear una incidencia',
    c1: 'Pulse **Crear** en la cabecera.',
    c2: 'Elija **Crear incidencia** o **Creación masiva desde CSV**.',
    c3: 'Rellene el formulario (proyecto, título, tipo, fecha límite, etc.) y envíe.',
    createTip: 'También puede crear desde el tablero del proyecto o el backlog.',
    typesTitle: 'Tipos de incidencia',
    typeTask: '**Tarea** – Trabajo general',
    typeStory: '**Historia** – Funcionalidad orientada al usuario',
    typeBug: '**Error** – Algo que corregir',
    typeEpic: '**Épica** – Gran iniciativa; otras incidencias pueden enlazar',
    typeSub: '**Subtarea** – Hija de otra incidencia',
    fieldsTitle: 'Qué puede configurar',
    fieldsBody:
      'Título, descripción, asignado, prioridad, estado y fecha límite son lo básico. También etiquetas, componentes, versiones, enlace padre o épica, estimaciones y adjuntos. Al crear puede añadir una **incidencia vinculada** y el tipo de enlace (p. ej. Bloquea, Está bloqueada por, Relacionada con, Duplicado); el tipo se recuerda para el siguiente enlace. Abra una incidencia para editar todo y comentar.',
  },
  backlog: {
    intro:
      'Vea incidencias fuera de un sprint y gestione sprints. Arrastre para reordenar el backlog o meter en un sprint.',
    b1: 'Arrastre incidencias a un sprint para planificar.',
    b2: 'Cree sprints y muévalos de planificado → activo → completado.',
    b3: 'Devuelva incidencias al backlog si hace falta.',
    b4:
      'Backlogs grandes usan desplazamiento virtualizado; el arrastre mantiene el mismo ordenamiento.',
  },
  'project-management': {
    intro:
      'Para administradores y gestores de proyecto. Abra **Gestión de proyectos** desde la barra lateral para ver todos los proyectos y la hoja de ruta de alto nivel.',
    allTitle: 'Todos los proyectos',
    allBody:
      'La tabla lista cada proyecto de la organización. Puede crear proyecto, exportar CSV y abrir tablero, backlog, hoja de ruta o documentos. Use **Gestionar miembros** por proyecto.',
    archivedBody:
      'Active **Mostrar archivados** para incluir proyectos archivados. Los admins de org y de proyecto archivan o restauran desde **Acciones** — los archivados no aparecen en la lista por defecto ni en la mayoría de selectores hasta restaurarlos.',
    roadmapTitle: 'Vista de hoja de ruta',
    roadmapLead:
      'Arriba, la **Vista de hoja de ruta** muestra, por proyecto:',
    ro1: '**Etapa actual** – Última versión publicada (si existe).',
    ro2: '**Próximo despliegue** – Nombre de la siguiente versión planificada.',
    ro3: '**Fecha planificada** – Fecha de publicación de esa versión si está definida.',
    roadmapFoot:
      'Así ve el estado de cada proyecto y el próximo release. Pulse el nombre del proyecto o **Ver hoja de ruta completa** para la hoja de ruta entre proyectos o la del proyecto.',
    roadmapNote:
      'Las versiones y fechas se configuran por proyecto (Ajustes de administración o del proyecto). Añada versiones y **Versión de corrección** en incidencias para llenar la hoja de ruta.',
  },
  roadmap: {
    intro:
      'Las hojas de ruta muestran versiones y entregas. FlowBoard tiene hoja de ruta por proyecto y otra entre proyectos para toda la org.',
    projTitle: 'Hoja de ruta del proyecto',
    projLead: 'Abra un proyecto y elija **Hoja de ruta** en la barra lateral. Verá:',
    pr1:
      'Una tarjeta con **Etapa actual**, **Próximo despliegue** y **Fecha planificada**.',
    pr2:
      '**Vista de lista** – Línea temporal de versiones con descripciones, fechas de inicio/publicación e incidencias (Versión de corrección / Afecta a versión).',
    pr3:
      '**Vista de línea temporal** – Versiones como barras; arrastre insignias de incidencia entre versiones para cambiar la **Versión de corrección**.',
    fixBody:
      'Añada versiones en el proyecto (u org) y establezca **Versión de corrección** / Afecta a versión en incidencias.',
    crossTitle: 'Hoja de ruta entre proyectos',
    crossBody:
      'Desde la barra lateral, **Hoja de ruta entre proyectos** lista todas las versiones de todos los proyectos. Cada fila muestra proyecto, versión, fecha y recuentos (Corrección / Afecta). Pulse un proyecto para abrir su hoja de ruta.',
    tip: 'Use la hoja de ruta para alinear interesados sobre el estado y el próximo despliegue. Defina fechas de inicio y publicación en versiones para líneas temporales precisas.',
  },
  calendar: {
    body: 'Las incidencias con fecha límite aparecen en el calendario. Cambie de mes para planificar o detectar retrasos.',
  },
  'list-view': {
    intro:
      'Tabla de todas las incidencias del proyecto. Ordene, filtre y edite en masa estado o asignado.',
    lv1: 'Use filtros avanzados por estado, asignado, etiqueta, etc.',
    lv2: 'Seleccione varias incidencias para cambiar estado, asignado o borrar de una vez.',
    lvBulk:
      'La edición masiva actualiza muchas incidencias. Desde la lista de un proyecto también puede poner etiquetas, componentes, versiones de corrección y resolución.',
    lv3:
      'Con listas grandes use **paginación** abajo para cambiar de página y el tamaño de página.',
    lv4:
      '**Diseño guardado** guarda filtro, búsqueda, orden y columnas en este navegador (aparte de filtros guardados en servidor). Active la columna **SLA** para ver incumplimientos activos.',
    tip: 'La vista lista es ideal para comparar muchas incidencias o actualizaciones masivas.',
  },
  'dependency-graph': {
    body: 'Mapa visual de cómo están enlazadas las incidencias (bloquea, bloqueada por, relacionada). Útil para impacto y rutas críticas.',
  },
  docs: {
    intro:
      'Cada proyecto tiene **Documentos** (tipo Confluence) para especificaciones y notas. Abra un proyecto y vaya a **Documentos** en la barra lateral.',
    tplTitle: 'Plantillas y etiquetas',
    tplBody:
      'Al crear o editar puede elegir una **plantilla** (p. ej. Notas de reunión, Spec) y **etiquetas** para organizar y buscar.',
    sidebarTitle: 'Barra lateral izquierda',
    sidebarBody:
      'Se muestra un árbol de páginas a la izquierda. **Nueva página** para crear; cuadro de búsqueda por título. Expanda o colapse carpetas; pulse el título para abrir.',
    pageTitle: 'En la página',
    d1: '**En esta página** – Índice fijo desde encabezados H2/H3 al ver (no al editar).',
    d2: '**Páginas hijas** – Si hay subpáginas, aparecen debajo con enlaces.',
    d3: '**Editar** – Título, página superior y contenido. Barra de herramientas para formato, enlaces e imágenes.',
    d4: '**Historial** – Ver y restaurar versiones anteriores.',
    d5: '**Exportar** – Descargar la página como Markdown.',
    d6: 'Los comentarios están abajo; añada y lea respuestas.',
    tip: 'Pulse **Ctrl + S** en el editor para guardar sin salir.',
  },
  reports: {
    intro:
      'Desde un proyecto, abra **Informes** para velocidad, flujo acumulado, gráficos de control, progreso de épica, tiempo, constructor de informes y vista **Workflow** (Gantt). Exporte datos o el gráfico para compartir.',
    typesTitle: 'Tipos de informe',
    r1: '**Velocidad** – Trabajo completado por sprint.',
    r2: '**Flujo acumulado** – Cómo se mueven las incidencias por estado en el tiempo.',
    r3: '**Gráfico de control** – Tiempo de ciclo de incidencias completadas.',
    r4:
      '**Informe de épica** – Desplegable **Épica** para una épica o todas. Exporte desde el botón.',
    r5: '**Seguimiento de tiempo** – Estimado frente a dedicado por tipo y usuario.',
    r6:
      '**Workflow (Gantt)** – Línea temporal con fechas límite: jerarquía (épicas → tareas → subtareas), barras de progreso, líneas de dependencia (padre-hijo y enlaces como «bloquea») e hitos (mismo día como rombos). Zoom (Día / Semana / Mes) y **Exportar como imagen** en PNG. Colores según estado del proyecto.',
    r7: '**Constructor de informes** – Agrupe por estado, tipo, prioridad o asignado.',
    tip: 'Use velocidad y flujo acumulado para ver rendimiento del equipo y cuellos de botella.',
  },
  dashboards: {
    body: 'Vaya a **Paneles personalizados** para crear su panel con widgets (por proyecto, grupo o asignado). Controle quién ve cada panel por grupo o rol.',
  },
  'admin-settings': {
    intro:
      'Abra **Ajustes de administración** desde la barra lateral para la organización y la configuración de proyectos.',
    orgTitle: 'Organización',
    orgBody: 'Edite el nombre de la org, miembros e invitaciones pendientes.',
    projCfgTitle: 'Configuración de proyectos',
    projCfgBody:
      'Gestione **Etiquetas**, **Componentes**, **Versiones** y **Campos personalizados** en los proyectos.',
    tplTitle: 'Plantillas',
    tplBody:
      'Plantillas de **proyecto** e **incidencia** definen flujos y campos por defecto.',
    autoTitle: 'Automatización',
    autoBody:
      'Reglas cuando las incidencias cumplen condiciones (p. ej. cambio de estado, asignado). Disparadores y acciones. Icono **Editar** (lápiz) en la tabla para nombre, descripción, disparador o activado; **Eliminar** quita la regla.',
    groupsTitle: 'Grupos',
    groupsBody:
      'Cree grupos, añada miembros y asígnelos a proyectos (en Gestionar miembros) para el mismo rol de proyecto.',
    rolesTitle: 'Roles y registro de auditoría',
    rolesBody: 'Roles personalizados y registro de auditoría de cambios importantes.',
    systemTitle: 'Sistema (solo superadmin)',
    systemBody:
      'La pestaña **Sistema** lista organizaciones. Puede fijar el **plan** (Free, Pro, Enterprise) e invitar empresas.',
  },
  glossary: {
    intro: 'Definiciones breves de términos en FlowBoard.',
    g1: '**Incidencia** – Elemento de trabajo: tarea, historia, error, épica o subtarea.',
    g2: '**Épica** – Gran iniciativa; otras incidencias pueden enlazar.',
    g3: '**Sprint** – Conjunto acotado en el tiempo (p. ej. 2 semanas).',
    g4: '**Backlog** – Incidencias aún no en un sprint.',
    g5: '**Versión** – Entrega o hito (p. ej. v1.0). Fechas opcionales; puede marcarse Publicada.',
    g6: '**Versión de corrección** – Versión donde se planifica corregir o entregar. Visible en la hoja de ruta.',
    g7: '**Afecta a versión** – Versiones que afecta la incidencia (p. ej. errores). También en la hoja de ruta.',
    g8: '**Tablero Kanban** – Columnas por estado; arrastre tarjetas.',
    g9: '**Flujo de trabajo** – Estados y transiciones (p. ej. Por hacer → En curso → Hecho).',
    g10: '**Asignado** – Persona responsable de la incidencia.',
    g11: '**Observadores** – Usuarios notificados de actualizaciones.',
  },
  'subscription-plans': {
    body: 'El plan Free limita proyectos y usuarios; planes superiores permiten más (incluido ilimitado). Al alcanzar el límite, la creación se bloquea hasta actualizar. Los superadmins fijan el plan en Ajustes de administración → Sistema.',
  },
  'api-integrations': {
    intro:
      'FlowBoard expone una API REST para incidencias, proyectos, informes, webhooks y más. Use la URL base de su instancia con un token válido (p. ej. tras iniciar sesión).',
    webhooksTitle: 'Webhooks',
    webhooksBody:
      'Los admins de org configuran webhooks salientes en **Admin → Webhooks**. Envían POST HTTP en eventos de incidencias y proyectos; opcionalmente secreto HMAC. Para Slack, añada un webhook con la URL de Slack (o reglas de automatización con acción Webhook).',
    openapiNote:
      'Especificación OpenAPI 3.0 en **/api/docs/openapi.json** con el backend en ejecución; úsela con Swagger u otras herramientas.',
  },
};

export default helpSectionsEs;
